import React, { useRef, useEffect, useState } from 'react';
import { TrackingResult, BoundingBox } from '../../core/types/tracking';
import { AudioFeatureWindow } from '../../core/types/audio';

interface LiveViewProps {
  isStereoMode: boolean;
  trackingResultA: TrackingResult | null;
  trackingResultB: TrackingResult | null;
  audioFeatures: AudioFeatureWindow | null;
  showMask: boolean;
  onToggleMask: () => void;
  videoElementA: HTMLVideoElement | null;
  videoElementB: HTMLVideoElement | null;
  maskImageDataA?: ImageData;
  maskImageDataB?: ImageData;
  onSelectRoiA?: (roi: BoundingBox) => void;
  onSelectRoiB?: (roi: BoundingBox) => void;
}

export const LiveView: React.FC<LiveViewProps> = ({
  isStereoMode,
  trackingResultA,
  trackingResultB,
  audioFeatures,
  showMask,
  onToggleMask,
  videoElementA,
  videoElementB,
  maskImageDataA,
  maskImageDataB,
  onSelectRoiA,
  onSelectRoiB,
}) => {
  const canvasRefA = useRef<HTMLCanvasElement>(null);
  const canvasRefB = useRef<HTMLCanvasElement>(null);

  // History trail for Camera A & B
  const trailRefA = useRef<Array<{ x: number; y: number }>>([]);
  const trailRefB = useRef<Array<{ x: number; y: number }>>([]);

  // ROI selection state for Camera A
  const [isSelectingRoiA, setIsSelectingRoiA] = useState(false);
  const [roiStartA, setRoiStartA] = useState<{ x: number; y: number } | null>(null);
  const [roiCurrentA, setRoiCurrentA] = useState<{ x: number; y: number } | null>(null);

  // ROI selection state for Camera B
  const [isSelectingRoiB, setIsSelectingRoiB] = useState(false);
  const [roiStartB, setRoiStartB] = useState<{ x: number; y: number } | null>(null);
  const [roiCurrentB, setRoiCurrentB] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (trackingResultA && trackingResultA.status === 'tracking') {
      trailRefA.current.push({
        x: trackingResultA.filteredPosition.x,
        y: trackingResultA.filteredPosition.y,
      });
      if (trailRefA.current.length > 30) trailRefA.current.shift();
    }
  }, [trackingResultA]);

  useEffect(() => {
    if (trackingResultB && trackingResultB.status === 'tracking') {
      trailRefB.current.push({
        x: trackingResultB.filteredPosition.x,
        y: trackingResultB.filteredPosition.y,
      });
      if (trailRefB.current.length > 30) trailRefB.current.shift();
    }
  }, [trackingResultB]);

  // Render Canvas Loop for Cam A
  useEffect(() => {
    const canvas = canvasRefA.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (videoElementA && videoElementA.readyState >= 2) {
      canvas.width = videoElementA.videoWidth || 640;
      canvas.height = videoElementA.videoHeight || 480;

      if (showMask && maskImageDataA) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = maskImageDataA.width;
        tempCanvas.height = maskImageDataA.height;
        const tempCtx = tempCanvas.getContext('2d');
        if (tempCtx) {
          tempCtx.putImageData(maskImageDataA, 0, 0);
          ctx.drawImage(tempCanvas, 0, 0, canvas.width, canvas.height);
        }
      } else {
        ctx.drawImage(videoElementA, 0, 0, canvas.width, canvas.height);
      }

      const scaleX = canvas.width / (maskImageDataA?.width || canvas.width);
      const scaleY = canvas.height / (maskImageDataA?.height || canvas.height);

      // Draw Trail A
      if (trailRefA.current.length > 1) {
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(132, 204, 22, 0.8)';
        ctx.lineWidth = 3;
        for (let i = 0; i < trailRefA.current.length; i++) {
          const pt = trailRefA.current[i];
          const px = pt.x * scaleX;
          const py = pt.y * scaleY;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.stroke();
      }

      // Draw Bounding Box & Target Center A
      if (trackingResultA && trackingResultA.status === 'tracking') {
        const bbox = trackingResultA.bbox;
        const bx = bbox.x * scaleX;
        const by = bbox.y * scaleY;
        const bw = bbox.width * scaleX;
        const bh = bbox.height * scaleY;

        ctx.strokeStyle = '#84cc16';
        ctx.lineWidth = 2;
        ctx.strokeRect(bx, by, bw, bh);

        const cx = trackingResultA.filteredPosition.x * scaleX;
        const cy = trackingResultA.filteredPosition.y * scaleY;

        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.arc(cx, cy, 5, 0, 2 * Math.PI);
        ctx.fill();

        ctx.fillStyle = '#84cc16';
        ctx.font = '12px sans-serif';
        ctx.fillText(
          `Area: ${trackingResultA.areaPx} | Circ: ${trackingResultA.circularity.toFixed(2)}`,
          bx,
          by > 15 ? by - 5 : by + bh + 15
        );
      }

      // Draw ROI Selection Box A
      if (isSelectingRoiA && roiStartA && roiCurrentA) {
        const rx = Math.min(roiStartA.x, roiCurrentA.x);
        const ry = Math.min(roiStartA.y, roiCurrentA.y);
        const rw = Math.abs(roiCurrentA.x - roiStartA.x);
        const rh = Math.abs(roiCurrentA.y - roiStartA.y);

        ctx.strokeStyle = '#0ea5e9';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 4]);
        ctx.strokeRect(rx, ry, rw, rh);
        ctx.setLineDash([]);
      }
    }
  }, [videoElementA, trackingResultA, showMask, maskImageDataA, isSelectingRoiA, roiStartA, roiCurrentA]);

  // Render Canvas Loop for Cam B
  useEffect(() => {
    if (!isStereoMode) return;
    const canvas = canvasRefB.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (videoElementB && videoElementB.readyState >= 2) {
      canvas.width = videoElementB.videoWidth || 640;
      canvas.height = videoElementB.videoHeight || 480;

      if (showMask && maskImageDataB) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = maskImageDataB.width;
        tempCanvas.height = maskImageDataB.height;
        const tempCtx = tempCanvas.getContext('2d');
        if (tempCtx) {
          tempCtx.putImageData(maskImageDataB, 0, 0);
          ctx.drawImage(tempCanvas, 0, 0, canvas.width, canvas.height);
        }
      } else {
        ctx.drawImage(videoElementB, 0, 0, canvas.width, canvas.height);
      }

      const scaleX = canvas.width / (maskImageDataB?.width || canvas.width);
      const scaleY = canvas.height / (maskImageDataB?.height || canvas.height);

      // Draw Trail B
      if (trailRefB.current.length > 1) {
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(14, 165, 233, 0.8)';
        ctx.lineWidth = 3;
        for (let i = 0; i < trailRefB.current.length; i++) {
          const pt = trailRefB.current[i];
          const px = pt.x * scaleX;
          const py = pt.y * scaleY;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.stroke();
      }

      // Draw Bounding Box & Target Center B
      if (trackingResultB && trackingResultB.status === 'tracking') {
        const bbox = trackingResultB.bbox;
        const bx = bbox.x * scaleX;
        const by = bbox.y * scaleY;
        const bw = bbox.width * scaleX;
        const bh = bbox.height * scaleY;

        ctx.strokeStyle = '#0ea5e9';
        ctx.lineWidth = 2;
        ctx.strokeRect(bx, by, bw, bh);

        const cx = trackingResultB.filteredPosition.x * scaleX;
        const cy = trackingResultB.filteredPosition.y * scaleY;

        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.arc(cx, cy, 5, 0, 2 * Math.PI);
        ctx.fill();

        ctx.fillStyle = '#0ea5e9';
        ctx.font = '12px sans-serif';
        ctx.fillText(
          `Area: ${trackingResultB.areaPx} | Circ: ${trackingResultB.circularity.toFixed(2)}`,
          bx,
          by > 15 ? by - 5 : by + bh + 15
        );
      }

      // Draw ROI Selection Box B
      if (isSelectingRoiB && roiStartB && roiCurrentB) {
        const rx = Math.min(roiStartB.x, roiCurrentB.x);
        const ry = Math.min(roiStartB.y, roiCurrentB.y);
        const rw = Math.abs(roiCurrentB.x - roiStartB.x);
        const rh = Math.abs(roiCurrentB.y - roiStartB.y);

        ctx.strokeStyle = '#0ea5e9';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 4]);
        ctx.strokeRect(rx, ry, rw, rh);
        ctx.setLineDash([]);
      }
    }
  }, [isStereoMode, videoElementB, trackingResultB, showMask, maskImageDataB, isSelectingRoiB, roiStartB, roiCurrentB]);

  // Mouse handlers for Cam A
  const handleMouseDownA = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRefA.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvas.height;
    setIsSelectingRoiA(true);
    setRoiStartA({ x, y });
    setRoiCurrentA({ x, y });
  };

  const handleMouseMoveA = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isSelectingRoiA || !canvasRefA.current) return;
    const rect = canvasRefA.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * canvasRefA.current.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvasRefA.current.height;
    setRoiCurrentA({ x, y });
  };

  const handleMouseUpA = () => {
    if (isSelectingRoiA && roiStartA && roiCurrentA && onSelectRoiA && canvasRefA.current) {
      const rx = Math.min(roiStartA.x, roiCurrentA.x);
      const ry = Math.min(roiStartA.y, roiCurrentA.y);
      const rw = Math.abs(roiCurrentA.x - roiStartA.x);
      const rh = Math.abs(roiCurrentA.y - roiStartA.y);

      if (rw > 5 && rh > 5) {
        const scaleX = (maskImageDataA?.width || canvasRefA.current.width) / canvasRefA.current.width;
        const scaleY = (maskImageDataA?.height || canvasRefA.current.height) / canvasRefA.current.height;

        onSelectRoiA({
          x: rx * scaleX,
          y: ry * scaleY,
          width: rw * scaleX,
          height: rh * scaleY,
        });
      }
    }
    setIsSelectingRoiA(false);
    setRoiStartA(null);
    setRoiCurrentA(null);
  };

  // Mouse handlers for Cam B
  const handleMouseDownB = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRefB.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvas.height;
    setIsSelectingRoiB(true);
    setRoiStartB({ x, y });
    setRoiCurrentB({ x, y });
  };

  const handleMouseMoveB = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isSelectingRoiB || !canvasRefB.current) return;
    const rect = canvasRefB.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * canvasRefB.current.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvasRefB.current.height;
    setRoiCurrentB({ x, y });
  };

  const handleMouseUpB = () => {
    if (isSelectingRoiB && roiStartB && roiCurrentB && onSelectRoiB && canvasRefB.current) {
      const rx = Math.min(roiStartB.x, roiCurrentB.x);
      const ry = Math.min(roiStartB.y, roiCurrentB.y);
      const rw = Math.abs(roiStartB.x - roiCurrentB.x);
      const rh = Math.abs(roiStartB.y - roiCurrentB.y);

      if (rw > 5 && rh > 5) {
        const scaleX = (maskImageDataB?.width || canvasRefB.current.width) / canvasRefB.current.width;
        const scaleY = (maskImageDataB?.height || canvasRefB.current.height) / canvasRefB.current.height;

        onSelectRoiB({
          x: rx * scaleX,
          y: ry * scaleY,
          width: rw * scaleX,
          height: rh * scaleY,
        });
      }
    }
    setIsSelectingRoiB(false);
    setRoiStartB(null);
    setRoiCurrentB(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          拖拽鼠标可在画面中框选标记进行 HSV 自动色彩估算
        </div>
        <button className="btn btn-outline" style={{ fontSize: '0.75rem', padding: '4px 10px' }} onClick={onToggleMask}>
          {showMask ? '切换为 原始画面' : '切换为 二值蒙版'}
        </button>
      </div>

      <div className="grid-live-views">
        {/* CAMERA A */}
        <div className="camera-card">
          <div className="camera-card-header">
            <span className="camera-title">摄像头 A (Camera A)</span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              Status: {trackingResultA?.status || 'idle'}
            </span>
          </div>

          <div className="camera-canvas-wrapper">
            <canvas
              ref={canvasRefA}
              className="camera-canvas"
              onMouseDown={handleMouseDownA}
              onMouseMove={handleMouseMoveA}
              onMouseUp={handleMouseUpA}
            />

            <div className="hud-overlay">
              <div>FPS: {videoElementA ? '30 (Active)' : '0'}</div>
              <div>
                Raw: ({trackingResultA?.rawPosition.x.toFixed(0) || 0}, {trackingResultA?.rawPosition.y.toFixed(0) || 0})
              </div>
              <div>
                Filtered: ({trackingResultA?.filteredPosition.x.toFixed(0) || 0}, {trackingResultA?.filteredPosition.y.toFixed(0) || 0})
              </div>
            </div>

            {trackingResultA?.status === 'lost' && (
              <div className="hud-target-lost">⚠️ 目标丢失 (TARGET LOST)</div>
            )}
          </div>
        </div>

        {/* CAMERA B (IF STEREO) */}
        {isStereoMode && (
          <div className="camera-card">
            <div className="camera-card-header">
              <span className="camera-title">摄像头 B (Camera B)</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Status: {trackingResultB?.status || 'idle'}
              </span>
            </div>

            <div className="camera-canvas-wrapper">
              <canvas
                ref={canvasRefB}
                className="camera-canvas"
                onMouseDown={handleMouseDownB}
                onMouseMove={handleMouseMoveB}
                onMouseUp={handleMouseUpB}
              />

              <div className="hud-overlay">
                <div>FPS: {videoElementB ? '30 (Active)' : '0'}</div>
                <div>
                  Raw: ({trackingResultB?.rawPosition.x.toFixed(0) || 0}, {trackingResultB?.rawPosition.y.toFixed(0) || 0})
                </div>
                <div>
                  Filtered: ({trackingResultB?.filteredPosition.x.toFixed(0) || 0}, {trackingResultB?.filteredPosition.y.toFixed(0) || 0})
                </div>
              </div>

              {trackingResultB?.status === 'lost' && (
                <div className="hud-target-lost">⚠️ 目标丢失 (TARGET LOST)</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* AUDIO METER HUD */}
      <div className="audio-meter-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.85rem' }}>
          <strong>实时麦克风音量 (Microphone dBFS)</strong>
          <span style={{ fontFamily: 'monospace', color: 'var(--accent-color)' }}>
            {audioFeatures ? `${audioFeatures.dbfs} dBFS` : '-120 dBFS'}
          </span>
        </div>

        <div className="dbfs-bar-bg">
          <div
            className="dbfs-bar-fill"
            style={{
              width: `${Math.max(0, Math.min(100, ((audioFeatures?.dbfs || -120) + 120) * (100 / 120)))}%`,
            }}
          />
        </div>

        <div className="metrics-row">
          <div className="metric-pill">RMS: {audioFeatures?.rms || 0}</div>
          <div className="metric-pill">主频 (F0): {audioFeatures?.dominantFrequencyHz || 0} Hz</div>
          <div className="metric-pill">频谱质心: {audioFeatures?.spectralCentroidHz || 0} Hz</div>
          <div className="metric-pill">过零率 (ZCR): {audioFeatures?.zeroCrossingRate || 0}</div>
        </div>
      </div>
    </div>
  );
};
